import React from 'react';

export interface PuToothDiagramProps {
  toothNumber: number;
  highlightColor: string;
  size?: 'sm' | 'md';
}

export function PuToothDiagram({ toothNumber, highlightColor, size = 'md' }: PuToothDiagramProps) {
  const dims = size === 'sm' ? { w: 220, h: 80, r: 4.5 } : { w: 280, h: 100, r: 5.5 };
  const teethTop = Array.from({ length: 16 }, (_, i) => i + 1);
  const teethBottom = Array.from({ length: 16 }, (_, i) => i + 17);
  const cx = dims.w / 2;
  const topY = dims.r + 4;
  const bottomY = dims.h - dims.r - 4;
  const spread = dims.w - dims.r * 2 - 8;

  const positionFor = (idx: number, total: number, baseY: number, arc: 'top' | 'bottom') => {
    const x = dims.r + 4 + (spread * idx) / (total - 1);
    const tt = (idx - (total - 1) / 2) / ((total - 1) / 2);
    const yOffset = arc === 'top' ? Math.abs(tt) * 5 : -Math.abs(tt) * 5;
    return { x, y: baseY + yOffset };
  };

  const renderRow = (arr: number[], baseY: number, arc: 'top' | 'bottom') =>
    arr.map((n, i) => {
      const { x, y } = positionFor(i, 16, baseY, arc);
      const on = n === toothNumber;
      return (
        <g key={n}>
          <circle
            cx={x}
            cy={y}
            r={dims.r}
            fill={on ? highlightColor : 'var(--cream-1)'}
            stroke={on ? highlightColor : 'var(--cream-shadow)'}
            strokeWidth={on ? 0 : 1}
          />
          {on ? (
            <text
              x={x}
              y={y + 0.5}
              textAnchor="middle"
              dominantBaseline="middle"
              fontSize={dims.r * 1.1}
              fontWeight={700}
              fill="#fff"
              fontFamily="var(--font-mono)"
            >
              {n}
            </text>
          ) : null}
        </g>
      );
    });

  return (
    <svg
      width="100%"
      height={dims.h}
      viewBox={`0 0 ${dims.w} ${dims.h}`}
      style={{ display: 'block' }}
      role="img"
      aria-label={`Tooth diagram showing tooth ${toothNumber} highlighted`}
    >
      {renderRow(teethTop, topY, 'top')}
      {renderRow(teethBottom, bottomY, 'bottom')}
      <path
        d={`M ${dims.r + 4} ${dims.h / 2} Q ${cx} ${dims.h / 2 + 3} ${dims.w - dims.r - 4} ${dims.h / 2}`}
        stroke="var(--cream-2)"
        strokeWidth="0.5"
        fill="none"
        strokeDasharray="2 2"
      />
      <text x={4} y={dims.h / 2 + 3} fontSize="9" fill="var(--ink-4)" fontFamily="var(--font-mono)" fontWeight={500}>
        R
      </text>
      <text
        x={dims.w - 8}
        y={dims.h / 2 + 3}
        fontSize="9"
        fill="var(--ink-4)"
        fontFamily="var(--font-mono)"
        fontWeight={500}
      >
        L
      </text>
    </svg>
  );
}
